<template>
  <div :class="'md-layout'">
    <div :class="'md-layout-item md-size-100'">
      <md-card>
        <md-card-header :class="'md-card-header-icon md-card-header-danger'">
          <div :class="'card-icon'">
            <md-icon>assignment</md-icon>
          </div>
          <h4 :class="'title'">
            List
            <md-button :class="'md-danger pull-right'" @click.prevent="isTryingToCreate = true"
              v-if="cannotView() && questions.length > 0">
              New
            </md-button>
          </h4>
        </md-card-header>
        <md-card-content>

          <!--Filters-->
          <div :class="'md-layout'">
            <div :class="'md-layout-item md-xsmall-size-100 md-small-size-50 md-medium-size-33 md-large-size-33'">
              <md-field md-clearable>
                <label>RQR Type</label>                
                <md-select placeholder="RQR Type" id="filter.rqr_type" v-model="filter.rqr_type" @input="getQuestions">
                  <md-option :value="code" :key="code" v-for="(type, code) in rqrTypesMap">
                    {{ type }}
                  </md-option>
                </md-select>
              </md-field>
            </div>
            <div :class="' md-layout-item md-xsmall-size-100 md-small-size-50 md-medium-size-33 md-large-size-33'">
              <md-field md-clearable>
                <label for="user-type">Section</label>
                <md-select id="filter.section" v-model="filter.section" @input="getQuestions">
                  <md-option v-for="(section, i) in sectionsForFilter" :key="i" :value="section.value">
                    {{ section.label }}
                  </md-option>
                  <md-option :value="'all'">All</md-option>
                </md-select>
              </md-field>
            </div>
            <div :class="' md-layout-item md-xsmall-size-100 md-small-size-50 md-medium-size-33 md-large-size-33'">
              <md-field>
                <label>Question</label>
                <md-input placeholder="Question" v-model="filter.question" @input="getQuestions"/>
              </md-field>
            </div>
          </div>

          <!--Table-->
          <md-table v-model="questions" table-header-color="orange">
            <md-empty-state md-icon="list" md-label="Questions" :md-description=" 'No questions found' +
              (this.filtersQuery ? ' with the searched terms' : '')" v-if="!isFetching">
              <md-button v-if="cannotView()" :class="'md-primary md-raised'" @click.prevent="isTryingToCreate = true">
                New
              </md-button>
            </md-empty-state>
            <md-table-row slot="md-table-row" slot-scope="{ item: question }">
              <md-table-cell md-label="RQR Type">{{ question.rqr_type | rqrType }}</md-table-cell>
              <md-table-cell md-label="Section">{{ question.section_text }}</md-table-cell>
              <md-table-cell md-label="Question">{{ question.text }}</md-table-cell>
              <md-table-cell md-label="Order">{{ question.question_order }}</md-table-cell>
              <md-table-cell v-if="cannotView()" md-label="Actions" md-numeric>
                <md-button :class="'md-simple md-just-icon'" :md-ripple="false" 
                  @click.prevent="tryToEdit(question)" title="Edit question">
                  <md-icon>edit</md-icon>
                </md-button>
                <md-button :class="'md-simple md-just-icon'" :md-ripple="false" 
                  @click.prevent="tryToDelete(question)" title="Delete question">
                  <md-icon>delete</md-icon>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
          <div :class="'md-flex text-center'" v-if="questions.length > 0">
            <Pagination :total="pagination.totalItems" :value="pagination.currentPage" 
              :perPage="pagination.perPage" @input="changePage" />
          </div>
          <div :class="'md-flex text-center'" v-if="isFetching">
            <md-progress-spinner :class="'md-accent'" :md-stroke="3" md-mode="indeterminate"></md-progress-spinner>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <!--Delete-->
    <md-dialog :md-active.sync="isTryingToDelete">
      <md-dialog-title>Delete question</md-dialog-title>
      <md-dialog-content>
        Are you sure you want to delete <b>{{ deletableQuestion.text }}</b>?
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isDeletingQuestion" @click.prevent="dismissDeleteModal" >Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="isDeletingQuestion" 
          @click.prevent="deleteQuestion(deletableQuestion)">
          {{ !isDeletingQuestion ? "Delete" : "Deleting..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <!--Create-->
    <md-dialog :md-active.sync="isTryingToCreate">
      <md-dialog-title>Create question</md-dialog-title>
      <md-dialog-content>
        <div :class="'md-layout'">
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="user-type">RQR Type</label>
              <md-select v-model="newQuestion.rqr_type" id="new.rqr_type">
                <md-option :value="code" :key="code" v-for="(type, code) in rqrTypesMap">
                  {{ type }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="user-type">Section</label>
              <md-select v-model="newQuestion.section" id="section">
                <md-option v-for="(section, i) in sections" :key="i" :value="section.value">
                  {{ section.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="section_description">Section description (optional)</label>
              <md-input id="section_description" v-model="newQuestion.section_description" :disabled="isCreatingQuestion"/>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field :class="$v.newQuestion.text.$error ? 'md-error' : ''">
              <label for="text">Question text</label>
              <md-input id="text" @blur="$v.newQuestion.text.$touch()" v-model="newQuestion.text"
                maxlength="500" :disabled="isCreatingQuestion"/>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="user-type">Type</label>
              <md-select v-model="newQuestion.type" id="type">
                <md-option v-for="(type, i) in types" :key="i" :value="type.value">
                  {{ type.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'" v-if="newQuestion.type === 1 || newQuestion.type === 7">
            <md-field>
              <md-input type="text" v-model="newOption" placeholder="Description"></md-input>
              <md-button :class="'md-dense md-raised md-primary'" @click.prevent="addOption">Add new option</md-button>
            </md-field>
            <br />
            <div>
              <md-checkbox v-for="(option, i) in options" :key="i" v-model="selectedOptions" :value="option.id || option.text">
                {{ option.text }}<br />
              </md-checkbox>
            </div>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isCreatingQuestion" @click.prevent="dismissCreatingModal">Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="isCreatingQuestion || !isValidForm" @click.prevent="createQuestion">
          {{ !isCreatingQuestion ? "Create" : "Creating..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <!--Edit-->
    <md-dialog :md-active.sync="isTryingToEdit">
      <md-dialog-title>Edit question</md-dialog-title>
      <md-dialog-content>
        <div :class="'md-layout'">
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="user-type">RQR Type</label>
              <md-select v-model="editableQuestion.rqr_type" id="section">
                <md-option :value="code" :key="code" v-for="(type, code) in rqrTypesMap">
                  {{ type }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="user-type">Section</label>
              <md-select v-model="editableQuestion.section" id="section">
                <md-option v-for="(section, i) in sections" :key="i" :value="section.value">
                  {{ section.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="section_description">Section description (optional)</label>
              <md-input id="section_description" v-model="editableQuestion.section_description" :disabled="isEditingQuestion"/>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field :class="$v.editableQuestion.text.$invalid ? 'md-error' : ''">
              <label for="text">Question Text</label>
              <md-input id="text" v-model="editableQuestion.text" :disabled="isEditingQuestion" maxlength="500"/>
            </md-field>
            <small :style="'color: #f44336'" v-if="!$v.editableQuestion.text.required" >The question is required</small>
            <small :style="'color: #f44336'" v-else-if="!$v.editableQuestion.text.maxLength" >Invalid question length</small>
          </div>
          <div :class="'md-layout-item md-size-100'">
            <md-field>
              <label for="user-type">Type</label>
              <md-select v-model="editableQuestion.type" id="type">
                <md-option v-for="(type, i) in types" :key="i" :value="type.value">
                  {{ type.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div :class="'md-layout-item md-size-100'" v-if="editableQuestion.type === 1 || editableQuestion.type === 7">
            <md-field>
              <md-input type="text" v-model="newOption" placeholder="Description"></md-input>
              <md-button :class="'md-dense md-raised md-primary'" @click.prevent="addOption">Add new option</md-button>
            </md-field>
            <br />
            <div>
              <md-checkbox v-for="(option, i) in options" :key="i" v-model="selectedOptions" :value="option.id || option.text">
                {{ option.text }}<br />
              </md-checkbox>
            </div>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isEditingQuestion" @click.prevent="dismissEditModal">Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="$v.editableQuestion.$invalid || isEditingQuestion"
          @click.prevent="editQuestion">
          {{ !isEditingQuestion ? "Update" : "Updating..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { maxLength, required } from "vuelidate/lib/validators";
import { date, rqrType, complianceAndQuality } from "@/filters";
import { rqrTypesMap } from "@/filters/rqrType";
import { Pagination } from "@/components";
import { camelToSnake } from "@/methods/api/requests";
import * as utils from "@/utils";
import * as filters from "@/filters";
import rqrTypeEnum from "@/enums/rqrType"

export default {
  created() {
    this.getQuestions();
  },

  data() {
    return {
      questions: [],
      isFetching: false,
      isTryingToDelete: false,
      deletableQuestion: {},
      isDeletingQuestion: false,
      isValidForm: false,
      isTryingToCreate: false,
      isCreatingQuestion: false,
      newQuestion: {
        text: "",
        section: "engagement_leader",
        rqr_type: rqrTypeEnum.COMPLICE,
        type: 1,
      },
      isTryingToEdit: false,
      editableQuestion: { rqr_type: rqrTypeEnum.COMPLICE },
      isEditingQuestion: false,
      edit: {
        rqr_type: rqrTypeEnum.COMPLICE,
        section: "overall",
      },
      options: [],
      newOption: "",
      selectedOptions: [],
      filter: {
        question: "",
        rqr_type: "",
        section: "",
      },
      pagination: {
        totalItems: "",
        currentPage: "",
        perPage: "",
      },
      errorResponses: {
        400: "The users spreadsheet is invalid",
        422: "There is an error with your file",
      },
      rqrTypesMap,
    };
  },

  components: {
    Pagination,
  },

  computed: {
    filtersQuery() {
      return filters.getFiltersQuery(this.filter);
    },

    paginationQuery() {
      return utils.getPaginationQuery(this.pagination.currentPage);
    },

    sections() {
      const secs = [];
      const obj = this.isTryingToCreate ? complianceAndQuality(Number(this.newQuestion.rqr_type))
        : complianceAndQuality(Number(this.editableQuestion.rqr_type));

      Object.entries(obj).forEach(([key, value]) =>
        secs.push({ value: key, label: value })
      );

      return secs;
    },

    sectionsForFilter() {
      const secs = [];
      let obj = {};

      if (this.filter.rqr_type === "")
        obj = complianceAndQuality(1, true);      
      else
        obj = complianceAndQuality(Number(this.filter.rqr_type));
      
      Object.entries(obj).forEach(([key, value]) =>
        secs.push({ value: key, label: value })
      );
      return secs;
    },

    types() {
      return [
        { value: 1, label: "Single Choice" },
        { value: 2, label: "File" },
        { value: 3, label: "Number" },
        { value: 4, label: "Open Answer" },
        { value: 5, label: "Money" },
        { value: 6, label: "Date" },
        { value: 7, label: "Single Choice (Long Form)" },
      ];
    },
  },

  filters: {
    date,
    rqrType,
    complianceAndQuality,
  },

  methods: {    
    addOption() {
      if (this.newOption !== "") {
        this.options.push({ text: this.newOption });
        this.newOption = "";
      } else {
        this.$toast.show(`Please fill in the option field`, "");
      }
    },

    cannotView() {
      return utils.getCannotView(this.$store.state.currentProfile)
    },

    changePage(toPage) {
      if (toPage === this.pagination.currentPage)
        return;

      this.pagination.currentPage = toPage;
      this.getQuestions();
    },

    createQuestion() {
      if (this.isCreatingQuestion)
        return;

      this.newQuestion.options = this.selectedOptions;
      this.$api.request
        .post(`questions`, this.newQuestion)
        .then(() => {
          this.dismissCreatingModal();
          this.isCreatingQuestion = false;
          this.getQuestions();
          this.$toast.show(
            `<b>Question</b> was created successfully`,
            "The question has been created successfully"
          );
          this.selectedOptions = [];
        })
        .catch((response) => {
          this.$toast.show(`<b>Error</b>`, response.data.message);
        })
        .finally(() => {
          this.isCreatingQuestion = false;
        });
    },

    deleteQuestion() {
      if (this.isDeletingQuestion)
        return;

      this.isDeletingUser = true;
      this.$api.request
        .delete(`questions/${this.deletableQuestion.id}`)
        .then(() => {
          this.isDeletingQuestion = false;
          this.$toast.show(
            `<b>Question</b> was deleted successfully`,
            "The question has been deleted successfully"
          );
          this.getQuestions();
        })
        .catch((error) => {
          this.$toast.warning(error.response.data.message, "");
        })
        .finally(() => {
          this.isDeletingQuestion = false;
          this.dismissDeleteModal();
        });
    },

    dismissCreatingModal() {
      if (this.isCreatingQuestion)
        return;
      
      this.options = []
      this.isTryingToCreate = false;
      this.selectedOptions = [];
      this.newQuestion = {
        text: "",
        section: "engagement_leader",
        rqr_type: rqrTypeEnum.COMPLICE,
        type: 1,
      };
      this.$v.newQuestion.$reset();
    },

    dismissDeleteModal() {
      if (this.isDeletingQuestion)
        return;

      this.isTryingToDelete = false;
    },

    dismissEditModal() {
      if (this.isEditingQuestion)
        return;      

      this.isTryingToEdit = false;
      this.selectedOptions = [];
    },

    editQuestion() {
      if (this.isEditingQuestion)
        return;

      this.isEditingQuestion = true;
      this.editableQuestion.options = this.selectedOptions;
      this.$api.request
        .put(`questions/${this.editableQuestion.id}`, this.editableQuestion)
        .then(() => {
          this.isEditingQuestion = false;
          this.getQuestions();
          this.$toast.show(
            `<b>Question</b> was updated successfully`,
            "The question has been updated successfully"
          );
        })
        .catch((error) => {
          this.$toast.warning(error.response.data.message, "");
        })
        .finally(() => {
          this.isEditingQuestion = false;
          this.dismissEditModal();
        });
    },

    getQuestions() {
      this.isFetching = true;
      this.$api
        .request(`questions?${this.filtersQuery}${this.paginationQuery}`)
        .then((response) => {
          this.questions = camelToSnake(response.data.data);
          this.pagination.totalItems = response.data.meta.total;
          this.pagination.currentPage = response.data.meta.currentPage;
          this.pagination.perPage = response.data.meta.perPage;
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    tryToDelete(question) {
      this.isTryingToDelete = true;
      this.deletableQuestion = question;
    },    

    tryToEdit(question) {
      this.isTryingToEdit = true;
      this.editableQuestion = Object.assign({}, question);
      this.edit.rqr_type = question.rqr_type;
      this.edit.section = question.section;
      this.edit.first = true;
      this.options = question.options;
      this.options.forEach((opt) => this.selectedOptions.push(opt.id));
    },

    validateNewQuestion() {
      this.isValidForm = !this.$v.newQuestion.$invalid;
      if (this.newQuestion.type === 1 || this.newQuestion.type === 7) {
        if (this.selectedOptions.length === 0)
          this.isValidForm = false;        
      }
    },    
  },

  validations: {
    newQuestion: {
      text: {
        required,
        maxLength: maxLength(500),
      },
      section: {
        required,
      },
    },

    editableQuestion: {
      text: {
        required,
        maxLength: maxLength(500),
      },
      section: {
        required,
      },
    },
  },

  watch: {
    "$v.newQuestion.$invalid": function () {
      this.validateNewQuestion();
    },
    selectedOptions: function () {
      if (this.isTryingToCreate)
        this.validateNewQuestion();      
    },
    "newQuestion.type": function () {
      this.validateNewQuestion();
    },
    "newQuestion.rqr_type": {
      handler: function (val, old) {
        if (val !== old) {
          this.newQuestion.section = "engagement_leader";
        }
      },
      deep: true,
    },
    "editableQuestion.rqr_type": {
      handler: function (val) {
        if (val !== this.edit.rqr_type)
          this.editableQuestion.section = "engagement_leader";
        else 
          this.editableQuestion.section = this.edit.section;        
      },
      deep: true,
    },
    "filter.rqr_type": {
      handler: function (val, old) {
        if (val !== old) {
          this.filter.section = "";
        }
      },
      deep: true,
    },
  },
};
</script>